import dayjs, { type Dayjs, type OpUnitType } from "dayjs";
import IntervalType from "src/constants/intervalType.enum";
import { getEndOfIsoYear } from "src/lib/date.helper";

export interface PollingSettings {
  staleTime: number;
  refetchInterval: number | false;
}

export const LIVE_POLLING: PollingSettings = { staleTime: 60_000, refetchInterval: 60_000 };
export const PAST_POLLING: PollingSettings = { staleTime: Number.POSITIVE_INFINITY, refetchInterval: false };

/**
 * Maps IntervalType to the dayjs OpUnitType used for endOf() boundary checks.
 * IsoYear handled custom upstream, as there is on fitting dayjs ISOYear boundary.
 */
export function intervalTypeToUnit(intervalType: IntervalType): OpUnitType {
  switch (intervalType) {
    case IntervalType.Month:
      return "month";
    case IntervalType.Week:
      return "week";
    case IntervalType.Hour:
      // Hourly queries fetch a full day of data; the day boundary drives staleness,
      // not the specific hour at which the page was loaded.
      return "day";
    default: // IntervalType.Day
      return "day";
  }
}

/**
 * Returns React Query polling overrides for queries that have both an intervalType and a date. 
 * Current ranges poll every 60 s (minimum DB write interval). 
 * Past ranges are immutable: polling is disabled entirely.
 *
 * Edge Case: For IsoYear queries the staleness boundary is the end of the *ISO year* (Sunday of its last ISO week). 
 */
export function getIntervalPollingSettings(intervalType: IntervalType, date: Dayjs | null): PollingSettings {
  const selectedDate = date ?? dayjs();
  const end =
    intervalType === IntervalType.IsoYear
      ? getEndOfIsoYear(selectedDate)
      : selectedDate.endOf(intervalTypeToUnit(intervalType));
  if (end.isBefore(dayjs())) return PAST_POLLING;
  return LIVE_POLLING;
}

/**
 * Returns React Query polling overrides for date-only queries (weather, events).
 * Past days are immutable; today and future poll every 60 s.
 */
export function getDateOnlyPollingSettings(date: Dayjs | null): PollingSettings {
  if (date && date.endOf("day").isBefore(dayjs())) return PAST_POLLING;
  return LIVE_POLLING;
}
